import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Award, MapPin, TrendingUp, ArrowRight, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SEO from '@/components/SEO';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const heroSlides = [
  { type: 'video', src: 'https://customer-assets.emergentagent.com/job_sbe-preview/artifacts/0xgjn13w_bars-of-gold-bullion-close-up-shot-2026-01-21-12-52-51-utc.mp4', title: 'Silver & Gold', subtitle: 'Value You Can Hold', cta: 'Explore Our Range', link: '/products' },
  { type: 'image', src: 'https://customer-assets.emergentagent.com/job_9291c06d-3d53-4aab-9c9b-bd4f24013ab1/artifacts/1f4h8q6x_shop-front-1.jpg', title: 'Visit Our Christchurch Store', subtitle: 'Real Advice for Real People', cta: 'Book an Appointment', link: '/contact' },
  { type: 'image', src: 'https://customer-assets.emergentagent.com/job_9291c06d-3d53-4aab-9c9b-bd4f24013ab1/artifacts/svochfzb_many-gold-bullions-brighting-on-the-table-2026-01-08-05-46-26-utc.jpg', title: 'Invest in Your Future', subtitle: 'Build Wealth Through Precious Metals', cta: 'Start Investing Today', link: '/products' },
];

function HeroSlider() {
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setCurrent(c => (c + 1) % heroSlides.length), 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="hero-slider" data-testid="hero-slider">
      {heroSlides.map((slide, i) => (
        <div key={slide.src} className={`hero-slide ${i === current ? 'active' : ''}`}>
          {slide.type === 'video' ? (
            <video src={slide.src} autoPlay muted loop playsInline className="w-full h-full object-cover" />
          ) : (
            <img src={slide.src} alt={slide.title} loading={i === 0 ? 'eager' : 'lazy'} />
          )}
          <div className="hero-overlay" style={{ background: 'linear-gradient(180deg, rgba(10,10,10,0.3) 0%, rgba(10,10,10,0.55) 100%)' }} />
        </div>
      ))}
      <div className="hero-content">
        <div className="flex flex-col items-center mb-4 animate-fade-in-up">
          <div className="coin-flip-container">
            <div className="coin-flip-inner">
              <img src="/rose.png" alt="SBE" className="coin-flip-front h-36 md:h-[11.25rem] w-36 md:w-[11.25rem] object-contain" data-testid="hero-label" />
              <img src="/gold-coin.png" alt="Gold Coin" className="coin-flip-back h-36 md:h-[11.25rem] w-36 md:w-[11.25rem] object-contain" />
            </div>
          </div>
          <span className="text-[#D4AF37] text-base md:text-2xl font-semibold tracking-wider mt-1" style={{ fontFamily: "Cinzel, serif" }}>
            Southern Bullion Exchange
          </span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#D4AF37] mb-4 animate-fade-in-up animate-delay-100" style={{ fontFamily: "Cinzel, serif" }} data-testid="hero-title">
          {heroSlides[current].title}
        </h1>
        <p className="text-lg md:text-xl text-white mb-8 max-w-xl animate-fade-in-up animate-delay-200" style={{ fontFamily: "'Poppins', sans-serif" }}>
          {heroSlides[current].subtitle}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animate-delay-300">
          <Link to={heroSlides[current].link} className="btn-gold text-sm inline-flex items-center gap-2" data-testid="hero-cta-primary">
            {heroSlides[current].cta} <ArrowRight size={16} />
          </Link>
          <Link to="/live-prices" className="btn-outline-gold text-sm inline-flex items-center gap-2" data-testid="hero-cta-secondary">
            View Live Prices
          </Link>
        </div>
      </div>
    </div>
  );
}

function WaveDivider() {
  const wavePath = "M-954,149.154H-3514V90.836c23.8-5.662,49.532-10.948,76.48-15.713,32.958-5.828,68.047-10.932,104.3-15.167C-3242.634,49.368-3143.733,44-3039.269,44c32.88,0,66.747.538,100.662,1.6,35.771,1.118,72.613,2.85,109.5,5.146,52.211,3.25,108.459,7.435,171.958,12.795,56.514,4.77,113.667,10.033,168.938,15.122l.13.012.368.034.041,0,.162.015c106.472,9.8,216.568,19.939,331.156,27.475,61.766,4.063,119.637,7.048,176.919,9.126,31.066,1.127,62.3,1.99,92.848,2.563,32.1.6,64.525.909,96.374.909,53.784,0,108.509-.872,162.654-2.591,57.313-1.82,116.083-4.656,174.678-8.431,61.532-3.964,124.975-9.1,188.566-15.26,66.385-6.432,135.126-14.2,204.315-23.078,26.682-3.425,60.363-8.638,106-16.4v96.118Z";
  return (
    <div className="wave-divider" data-testid="wave-divider">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 2560 105.153"
        preserveAspectRatio="none"
        className="w-full block"
        style={{ filter: 'drop-shadow(0px -10px 15px rgba(0,0,0,0.25))', pointerEvents: 'none' }}
      >
        <defs>
          <linearGradient id="silverGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ECECEE" />
            <stop offset="50%" stopColor="#ECECEE" />
            <stop offset="100%" stopColor="#ECECEE" />
          </linearGradient>
        </defs>
        <path
          d={wavePath}
          transform="translate(3514 -44)"
          fill="url(#silverGradient)"
        />
      </svg>
    </div>
  );
}

function TrustSection() {
  const items = [
    { icon: Shield, title: 'AML/CFT Compliant', desc: 'Full regulatory compliance', link: '/aml-cft' },
    { icon: MapPin, title: 'Christchurch Based', desc: 'Visit our Bishopdale store', link: '/about' },
    { icon: Award, title: 'Established 2025', desc: 'Trusted by local investors', link: '/about' },
    { icon: TrendingUp, title: 'Live NZD Pricing', desc: 'Transparent, fair prices', link: '/live-prices' },
  ];
  return (
    <section className="pt-24 pb-16 relative z-10" data-testid="trust-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {items.map((item, i) => (
            <a href={item.link} key={item.title} className="flex flex-col items-center text-center gap-3 group cursor-pointer no-underline" data-testid={`trust-item-${i}`}>
              <div className="trust-flip-container">
                <div className="trust-flip-inner">
                  <div className="trust-flip-front w-[6.8rem] h-[6.8rem] rounded-full flex items-center justify-center trust-gold-shine">
                    <item.icon size={47} className="text-[#0A0A0A]" />
                  </div>
                  <div className="trust-flip-back w-[6.8rem] h-[6.8rem] rounded-full bg-[#0A0A0A] flex items-center justify-center overflow-hidden">
                    <img src="/rose.png" alt="SBE" className="w-[6.2rem] h-[6.2rem] object-contain" />
                  </div>
                </div>
              </div>
              <h3 className="text-sm font-semibold group-hover:text-[#D4AF37] transition-colors" style={{ fontFamily: "'Poppins', sans-serif" }}>{item.title}</h3>
              <p className="text-xs text-gray-500" style={{ fontFamily: "'Poppins', sans-serif" }}>{item.desc}</p>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedProducts() {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    axios.get(`${API}/products?featured=true`).then(r => setProducts(r.data)).catch(() => {});
  }, []);

  if (!products.length) return null;

  return (
    <section className="py-20 md:py-32" data-testid="featured-products">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <div className="text-center mb-16">
          <p className="section-label mb-3">Our Products</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold" style={{ fontFamily: "Cinzel, serif" }}>Featured Bullion</h2>
          <p className="text-base text-gray-500 mt-4 max-w-2xl mx-auto" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Explore our selection of investment-grade gold and silver products.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
          {products.slice(0, 5).map(product => (
            <div key={product.id} className="group bg-white border border-gray-200 rounded-md overflow-hidden transition-all hover:shadow-lg hover:-translate-y-0.5 hover:border-gray-300 flex flex-col h-full" data-testid={`product-card-${product.id}`}>
              <div className="aspect-square overflow-hidden bg-gray-50 relative p-5 sm:p-6">
                <img src={product.image_url} alt={product.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                {product.purity && (
                  <span className="absolute bottom-2 left-2 text-[9px] uppercase tracking-wider text-white font-medium bg-black/55 px-1.5 py-0.5 rounded" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    {product.purity} fine
                  </span>
                )}
              </div>
              <div className="p-3 flex flex-col flex-1 border-t border-gray-100">
                <span className="text-[10px] uppercase tracking-wider text-[#D4AF37] font-semibold" style={{ fontFamily: "'Poppins', sans-serif" }}>{product.category}</span>
                <h3 className="text-sm font-semibold mt-0.5 mb-1 leading-tight line-clamp-2 min-h-[2.5rem]" style={{ fontFamily: "'Poppins', sans-serif" }}>{product.name}</h3>
                {product.mint && (
                  <p className="text-[10px] text-gray-400 mb-2 line-clamp-1" style={{ fontFamily: "'Poppins', sans-serif" }}>{product.mint}</p>
                )}
                {product.pricing_mode === 'fixed' && product.fixed_price_nzd && (
                  <p className="text-base font-bold text-[#0A0A0A] mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    NZ${product.fixed_price_nzd.toLocaleString('en-NZ', { minimumFractionDigits: 2 })}
                  </p>
                )}
                <div className="mt-auto pt-2">
                  {product.pricing_mode === 'fixed' && product.fixed_price_nzd ? (
                    <Link to={`/products?product=${product.id}`} className="btn-gold w-full text-center text-xs h-9 flex items-center justify-center" data-testid={`buy-now-${product.id}`}>
                      Buy Now
                    </Link>
                  ) : (
                    <Link to={`/products?product=${product.id}`} className="btn-gold w-full text-center text-xs h-9 flex items-center justify-center" data-testid={`request-quote-${product.id}`}>
                      Request Quote
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-12">
          <Link to="/products" className="btn-outline-dark text-sm inline-flex items-center gap-2" data-testid="view-all-products">
            View All Products <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

const mintLogos = [
  { name: 'NZ Mint', src: '/mint-logos/nz-mint.webp' },
  { name: 'Dillon Gage', src: '/mint-logos/dillon-gage-dark.png' },
  { name: 'The Perth Mint', src: '/mint-logos/perth-mint.webp' },
  { name: 'ABC Bullion', src: '/mint-logos/abc.webp' },
  { name: 'Valcambi Suisse', src: '/mint-logos/valcambi.webp' },
  { name: 'Royal Canadian Mint', src: '/mint-logos/rcm.webp' },
  { name: 'The Royal Mint', src: '/mint-logos/royal-mint.webp' },
  { name: 'South African Mint', src: '/mint-logos/sa-mint.webp' },
  { name: 'Münze Österreich', src: '/mint-logos/austrian-mint.webp' },
  { name: 'Golden State Mint', src: '/mint-logos/golden-state.webp' },
  { name: 'United States Mint', src: '/mint-logos/us-mint.webp' },
  { name: 'Morris & Watson', src: '/mint-logos/morris-watson.webp' },
  { name: 'PAMP Suisse', src: '/mint-logos/pamp.webp' },
  { name: 'Ainslie Bullion', src: '/mint-logos/ainslie.webp' },
];

function MintBanner() {
  const doubled = [...mintLogos, ...mintLogos];
  return (
    <section className="py-10 md:py-14" style={{ background: '#FFFFFF', boxShadow: '0 0 20px rgba(0,0,0,0.06)' }} data-testid="mint-banner">
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-8 text-center">
        <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>Trusted Worldwide Partners</p>
        <h3 className="text-lg font-semibold text-gray-700" style={{ fontFamily: "'Poppins', sans-serif" }}>Sourced From Leading Global Mints & Refiners</h3>
      </div>
      <div className="mint-marquee">
        <div className="mint-marquee-track">
          {doubled.map((mint, i) => (
            <div key={`${mint.name}-${i}`} className="mint-marquee-logo" title={mint.name}>
              <img src={mint.src} alt={mint.name} loading="lazy" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SellCTA() {
  return (
    <section className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="sell-cta-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col lg:flex-row items-center gap-12">
        <div className="flex-1">
          <p className="section-label mb-3">Sell Your Bullion</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: "Cinzel, serif" }}>
            Looking to Sell Gold or Silver?
          </h2>
          <p className="text-base text-gray-400 mb-8 max-w-lg" style={{ fontFamily: "'Poppins', sans-serif" }}>
            We buy gold, silver, precious metals, and jewellery at competitive prices. Get an indicative quote online or visit our Bishopdale store for an immediate valuation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/sell" className="btn-gold text-sm inline-flex items-center gap-2" data-testid="sell-cta-button">
              Get a Sell Quote <ArrowRight size={16} />
            </Link>
            <a href="tel:0211531763" className="btn-outline-gold text-sm inline-flex items-center gap-2">
              Call Us Now
            </a>
          </div>
        </div>
        <div className="flex-1 max-w-md">
          <img src="/sbe-store.webp" alt="Southern Bullion Exchange Store" className="rounded-sm shadow-2xl" loading="lazy" />
        </div>
      </div>
    </section>
  );
}

function EducationTeaser() {
  const [articles, setArticles] = useState([]);
  useEffect(() => {
    axios.get(`${API}/articles`).then(r => setArticles(r.data.slice(0, 3))).catch(() => {});
  }, []);

  if (!articles.length) return null;

  return (
    <section className="py-20 md:py-32" data-testid="education-teaser">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12">
          <div>
            <p className="section-label mb-3">Education Hub</p>
            <h2 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "Cinzel, serif" }}>Learn About Bullion</h2>
          </div>
          <Link to="/education" className="btn-outline-dark text-sm inline-flex items-center gap-2" data-testid="view-all-articles">
            All Articles <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {articles.map(article => (
            <Link key={article.slug} to={`/education/${article.slug}`} className="group" data-testid={`article-card-${article.slug}`}>
              <div className="aspect-[16/10] overflow-hidden rounded-sm mb-4 bg-gray-100 border-[3px] border-[#cca900] relative">
                <img src={article.image_url} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                {article.external_video_url && (
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-[#D4AF37] flex items-center justify-center">
                      <Play size={22} className="text-[#0A0A0A] ml-0.5" fill="#0A0A0A" />
                    </div>
                  </div>
                )}
              </div>
              <span className="text-xs uppercase tracking-wider text-[#D4AF37] font-semibold" style={{ fontFamily: "'Poppins', sans-serif" }}>{article.category}</span>
              <h3 className="text-lg font-semibold mt-1 group-hover:text-[#D4AF37] transition-colors" style={{ fontFamily: "'Poppins', sans-serif" }}>{article.title}</h3>
              <p className="text-sm text-gray-500 mt-2 line-clamp-2" style={{ fontFamily: "'Poppins', sans-serif" }}>{article.excerpt}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const [reviews, setReviews] = useState([]);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    axios.get(`${API}/testimonials`).then(r => setReviews(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (reviews.length <= 1) return;
    const timer = setInterval(() => setCurrent(c => (c + 1) % reviews.length), 5000);
    return () => clearInterval(timer);
  }, [reviews.length]);

  if (!reviews.length) return null;
  const t = reviews[current];

  return (
    <section className="py-10 md:py-16" data-testid="testimonials-section">
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
        <p className="section-label mb-3">Google Reviews</p>
        <h2 className="text-3xl sm:text-4xl font-bold mb-12" style={{ fontFamily: "Cinzel, serif" }}>What Our Clients Say</h2>
        <div className="relative">
          <div className="flex justify-center mb-4">
            <span style={{ color: '#D4AF37', fontSize: '30px', lineHeight: 1 }}>{'★'.repeat(t.rating || 5)}</span>
          </div>
          <blockquote className="text-lg md:text-xl text-gray-700 italic mb-6 leading-relaxed" style={{ fontFamily: "'Poppins', sans-serif" }}>
            "{t.text}"
          </blockquote>
          <p className="font-semibold" style={{ fontFamily: "'Poppins', sans-serif" }}>{t.name}</p>
          <p className="text-sm text-gray-400 mt-1" style={{ fontFamily: "'Poppins', sans-serif" }}>Google Review</p>
          <div className="flex justify-center gap-4 mt-8">
            <button onClick={() => setCurrent(c => (c - 1 + reviews.length) % reviews.length)} className="p-2 border border-gray-300 hover:border-[#D4AF37] transition-colors rounded-sm" data-testid="testimonial-prev">
              <ChevronLeft size={18} />
            </button>
            <button onClick={() => setCurrent(c => (c + 1) % reviews.length)} className="p-2 border border-gray-300 hover:border-[#D4AF37] transition-colors rounded-sm" data-testid="testimonial-next">
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function Newsletter() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success('Successfully subscribed to our newsletter!');
      setEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="newsletter-section">
      <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
        <p className="section-label mb-3">Stay Informed</p>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: "Cinzel, serif" }}>Subscribe to Our Newsletter</h2>
        <p className="text-gray-400 mb-8" style={{ fontFamily: "'Poppins', sans-serif" }}>
          Receive monthly market updates, price alerts, and investment insights tailored for New Zealand investors.
        </p>
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
          <Input type="email" placeholder="Enter your email" value={email} onChange={e => setEmail(e.target.value)} className="flex-1 bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 h-12" required data-testid="newsletter-email" />
          <Button type="submit" disabled={loading} className="btn-gold h-12 px-8" data-testid="newsletter-submit">
            {loading ? 'Subscribing...' : 'Subscribe'}
          </Button>
        </form>
        <p className="text-xs text-gray-600 mt-4">We respect your privacy. Unsubscribe at any time.</p>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <div data-testid="home-page">
      <SEO
        description="Christchurch's trusted gold, silver & platinum bullion dealer. Buy and sell investment-grade bullion at competitive NZD prices. AML/CFT compliant. Visit our Bishopdale store or request a quote online."
        path="/"
      />
      <div style={{ background: '#0A0A0A' }}>
        <HeroSlider />
        <WaveDivider />
      </div>
      <div className="relative z-10" style={{ background: '#ECECEE' }}>
        <TrustSection />
        <FeaturedProducts />
        <SellCTA />
        <EducationTeaser />
        <MintBanner />
        <Testimonials />
        <Newsletter />
      </div>
    </div>
  );
}
